'use strict';

import SettingsService from '@/services/settingsService';
import { defaultSettings } from '@/store/modules/settings';
import { STATUS_SUCCESS } from '@/constants/appConstants';
import AgentService from '@/services/agentService';

const requestCurrentUserSettings = ({ commit, rootGetters, dispatch }) => {
  const loggedInAgent = rootGetters['AGENT/loggedInAgent'];
  return SettingsService.get(loggedInAgent.id).then(response => {
    const settings = response.data;
    if (settings) {
      commit('setCurrentUserSettings', settings);
      return settings;
    } else {
      return dispatch('createCurrentUserSettings');
    }
  });
};

const setCurrentUserSettings = ({ commit }, settings) => {
  const newSettings = { ...defaultSettings, ...settings };
  commit('setCurrentUserSettings', newSettings);
};

const createCurrentUserSettings = ({ commit, rootGetters }) => {
  const settings = {
    ...defaultSettings,
    featuresSettings: {
      ...defaultSettings.featuresSettings,
      showApplicationTour: true,
      latestShowedFeatures: 'no-features'
    }
  };

  const defaultTimeZone = rootGetters['REFERENCES/defaultTimeZone'];

  if (defaultTimeZone) {
    settings.applicationSettings.timeZoneId = defaultTimeZone.value;
    settings.applicationSettings.timeZoneUtcOffset = defaultTimeZone.utc;
  }

  return SettingsService.create(rootGetters['AGENT/loggedInAgent'].id, settings).then(response => {
    commit('setCurrentUserSettings', settings);
    return response.data;
  });
};

const setSoftPhoneSettings = ({ commit }, softPhoneSettings) => {
  commit('setSoftPhoneSettings', softPhoneSettings);
};

const updateCurrentUserSettings = ({ commit, rootGetters }, settings) => {
  const loggedInAgent = rootGetters['AGENT/loggedInAgent'];
  return SettingsService.update(loggedInAgent.id, settings).then(response => {
    if (response.status === STATUS_SUCCESS) {
      commit('setCurrentUserSettings', response.data);
    }
    return response;
  });
};

const updateCurrentUserDashboardSettings = ({ commit, dispatch, getters }, dashboardSettings) => {
  const settings = {
    dashboardSettings
  };
  return dispatch('updateCurrentUserSettings', settings).then(response => {
    commit('updateCurrentUserDashboardSettings', response.data.dashboardSettings);
    return response;
  });
};

const updateCurrentSupervisorWallboardSettings = ({ commit, dispatch, getters }, supervisorWallboardSettings) => {
  const settings = {
    supervisorWallboardSettings
  };
  return dispatch('updateCurrentUserSettings', settings).then(response => {
    commit('updateCurrentSupervisorWallboardSettings', response.data.supervisorWallboardSettings);
    return response;
  });
};

const createCurrentUserDefaultDashboard = ({ dispatch, getters, rootGetters }) => {
  const defaultDashboard = rootGetters['DASHBOARD/defaultDashboard'];
  let businessProcessesStats = rootGetters['STATS/BUSINESS_PROCESSES_STATS'];
  let defaultBusinessProcessId = '';

  if (businessProcessesStats.length === 0) {
    businessProcessesStats = rootGetters['BUSINESS_PROCESSES/availableBusinessProcesses'];
  } else {
    defaultBusinessProcessId = businessProcessesStats[0].businessProcessId;
  }

  defaultDashboard.cards.map(card => {
    if (card.componentName !== 'EcsAgentKPICard' && card.componentName !== 'EcsQueueBusinessProcessSummaryCard') {
      card.startBusinessProcessId = defaultBusinessProcessId;
    }
    if (card.componentName === 'EcsQueueBusinessProcessSummaryCard') {
      card.startBusinessProcessId = businessProcessesStats.map(item => item.businessProcessId).join();
    }
    return card;
  });

  const settings = {
    'dashboardSettings.dashboards': [defaultDashboard]
  };

  return dispatch('updateCurrentUserSettings', settings).then(response => {
    dispatch('DASHBOARD/setSelectedDashboard', response.data.dashboardSettings.dashboards[0], { root: true });
  });
};

const updateCurrentUserDashboardCardBusinessProcess = ({ commit, dispatch, getters, rootGetters }, data) => {
  const { dashboardIndex, cardIndex, bpId } = data;
  const propertyPath = `dashboardSettings.dashboards.${dashboardIndex}.cards.${cardIndex}.startBusinessProcessId`;
  const settings = {
    [propertyPath]: bpId
  };
  return dispatch('updateCurrentUserSettings', settings).then(response => {
    commit('updateCurrentUserDashboardSettings', response.data.dashboardSettings);
    const selectedDashboardId = rootGetters['DASHBOARD/selectedDashboard']._id;
    const updatedSelectedDashboard = getters.dashboardSettings.dashboards.find(
      dashboard => dashboard._id === selectedDashboardId
    );
    dispatch('DASHBOARD/setSelectedDashboard', updatedSelectedDashboard, { root: true });
    return response;
  });
};

const updateSupervisorAdaptiveDashboardSettings = ({ dispatch }, { property, settings: dashboardSettings }) => {
  const settings = {
    [property]: dashboardSettings
  };
  return dispatch('updateCurrentUserSettings', settings);
};

const updateSupervisorAdaptiveCardSettings = ({ dispatch, getters }, cardSettings) => {
  const updatedSettings = getters.currentUserSettings;
  const dashboardIndex = updatedSettings.supervisorDashboardSettings.adaptiveDashboards.cards.findIndex(
    settings => settings.businessProcessId === cardSettings.businessProcessId
  );

  if (dashboardIndex === -1) {
    updatedSettings.supervisorDashboardSettings.adaptiveDashboards.cards.push(cardSettings);
  } else {
    updatedSettings.supervisorDashboardSettings.adaptiveDashboards.cards.splice(dashboardIndex, 1, cardSettings);
  }

  return dispatch('updateCurrentUserSettings', updatedSettings);
};

const updateSupervisorSummaryTable = ({ dispatch, getters }, supervisorSummaryTable) => {
  const updatedSettings = getters.currentUserSettings;
  updatedSettings.supervisorDashboardSettings.summaryTable = supervisorSummaryTable;
  dispatch('updateCurrentUserSettings', updatedSettings);
};

const updateSupervisorSummaryTableSettings = ({ dispatch, getters }, supervisorSummaryTableSettings) => {
  const updatedSettings = getters.currentUserSettings;
  updatedSettings.supervisorDashboardSettings.summaryTableSettings = supervisorSummaryTableSettings;
  dispatch('updateCurrentUserSettings', updatedSettings);
};

const enableOfflineMode = state => {
  state.commit('setOfflineMode', true);
  return true;
};

const disableOfflineMode = state => {
  state.commit('setOfflineMode', false);
  return false;
};

// Preferences Settings
const updateIncomingInteractionDisplay = ({ dispatch }, payload) => {
  const settings = {
    'preferencesSettings.incomingInteractionDisplay': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateAutoPlayRecordings = ({ dispatch }, payload) => {
  const settings = {
    'preferencesSettings.autoPlayRecordings': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateUseSoftPhone = ({ dispatch }, payload) => {
  const settings = {
    'preferencesSettings.useSoftPhone': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateAutoOpenInteractionDetails = ({ dispatch }, enabled) => {
  const settings = {
    'preferencesSettings.autoOpenInteractionDetails': enabled
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateAutoOffHookState = ({ dispatch }, enabled) => {
  const settings = {
    'preferencesSettings.autoOffHookState': enabled
  };
  dispatch('updateCurrentUserSettings', settings).then(() => {
    return enabled ? AgentService.enableAutoOffHook() : AgentService.disableAutoOffHook();
  });
};

const updatePauseKPIEvents = ({ dispatch }, liveKPIs) => {
  const settings = {
    'preferencesSettings.pauseKPIEvents': !liveKPIs
  };
  return dispatch('updateCurrentUserSettings', settings);
};

const updateMuteDTMF = ({ dispatch }, muteDTMF) => {
  const settings = {
    'preferencesSettings.muteDTMF': muteDTMF
  };
  return dispatch('updateCurrentUserSettings', settings);
};

const updateCallIncomingInteractionRingtones = ({ dispatch }, payload) => {
  const settings = {
    'preferencesSettings.incomingInteractionRingtones.call': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateChatIncomingInteractionRingtones = ({ dispatch }, payload) => {
  const settings = {
    'preferencesSettings.incomingInteractionRingtones.chat': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateEmailIncomingInteractionRingtones = ({ dispatch }, payload) => {
  const settings = {
    'preferencesSettings.incomingInteractionRingtones.email': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateSmsIncomingInteractionRingtones = ({ dispatch }, payload) => {
  const settings = {
    'preferencesSettings.incomingInteractionRingtones.sms': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateInteractionHistoryPeriod = ({ dispatch }, payload) => {
  const settings = {
    'preferencesSettings.interactionHistoryPeriod': payload
  };
  dispatch('updateCurrentUserSettings', settings).then(() => {
    dispatch('INTERACTIONS/requestAgentInteractionHistory', null, { root: true });
  });
};

const updateIdleTimeToAgentIntelligenceCenter = ({ dispatch }, payload) => {
  const settings = {
    'preferencesSettings.idleTimeToAgentIntelligenceCenter': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateAlertEndOfBreak = ({ dispatch }, payload) => {
  const settings = {
    'preferencesSettings.alertEndOfBreak': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

// Application Settings
const updateLanguage = ({ dispatch }, payload) => {
  const settings = {
    'applicationSettings.language': payload
  };
  return dispatch('updateCurrentUserSettings', settings);
};

const updateTimeFormat = ({ dispatch }, payload) => {
  const settings = {
    'applicationSettings.timeFormat': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateTimeZone = ({ dispatch }, payload) => {
  const settings = {
    'applicationSettings.timeZoneId': payload.value,
    'applicationSettings.timeZoneUtcOffset': payload.utc
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateShowAppCurrentTime = ({ dispatch }, showAppCurrentTime) => {
  const settings = {
    'preferencesSettings.showAppCurrentTime': showAppCurrentTime
  };
  return dispatch('updateCurrentUserSettings', settings);
};

// Agent Settings
const updateDefaultOutgoingCallBusinessProcessId = ({ dispatch }, payload) => {
  const settings = {
    'agentSettings.defaultOutgoingCallBusinessProcessId': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateDefaultCallingLineIdentificationPerBP = ({ dispatch }, payload) => {
  const settings = {
    'agentSettings.defaultCallingLineIdentificationPerBP': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateDefaultOutgoingEmailBusinessProcessId = ({ dispatch }, payload) => {
  const settings = {
    'agentSettings.defaultOutgoingEmailBusinessProcessId': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateDefaultOutgoingSmsBusinessProcessId = ({ dispatch }, payload) => {
  const settings = {
    'agentSettings.defaultOutgoingSmsBusinessProcessId': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

// Agent State

const updateLastSelectedTerminal = ({ dispatch, getters }, { terminalType, terminalURI, useInternalPhone }) => {
  const settings = {
    'agentStateSettings.lastSelectedTerminal': { terminalURI, terminalType, useInternalPhone }
  };
  dispatch('updateCurrentUserSettings', settings);
};

// Theme
const updateThemeMode = ({ dispatch }, payload) => {
  const settings = {
    'theme.mode': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateThemeId = ({ dispatch }, payload) => {
  const settings = {
    'theme._id': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

// FeaturesSettings
const updateShowApplicationTour = ({ dispatch }, payload) => {
  const settings = {
    'featuresSettings.showApplicationTour': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updateLatestShowedFeatures = ({ dispatch }, payload) => {
  const settings = {
    'featuresSettings.latestShowedFeatures': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const updatePinnedSideBarOnMaximizedMode = ({ dispatch }, payload) => {
  const settings = {
    'featuresSettings.pinnedSideBarOnMaximizedMode': payload
  };
  dispatch('updateCurrentUserSettings', settings);
};

const removeUserSettings = ({ commit }) => {
  commit('removeUserSettings');
};

export default {
  setCurrentUserSettings,
  requestCurrentUserSettings,
  createCurrentUserSettings,
  updateCurrentUserSettings,
  updateCurrentUserDashboardSettings,
  updateCurrentSupervisorWallboardSettings,
  updateSupervisorAdaptiveDashboardSettings,
  updateSupervisorAdaptiveCardSettings,
  updateSupervisorSummaryTable,
  enableOfflineMode,
  disableOfflineMode,
  updateIncomingInteractionDisplay,
  updateAutoPlayRecordings,
  updatePauseKPIEvents,
  updateMuteDTMF,
  updateUseSoftPhone,
  updateAutoOffHookState,
  updateAutoOpenInteractionDetails,
  updateCallIncomingInteractionRingtones,
  updateChatIncomingInteractionRingtones,
  updateEmailIncomingInteractionRingtones,
  updateInteractionHistoryPeriod,
  updateIdleTimeToAgentIntelligenceCenter,
  updateAlertEndOfBreak,
  updateLanguage,
  updateTimeFormat,
  updateTimeZone,
  updateShowAppCurrentTime,
  updateDefaultOutgoingCallBusinessProcessId,
  updateDefaultCallingLineIdentificationPerBP,
  updateDefaultOutgoingEmailBusinessProcessId,
  updateLastSelectedTerminal,
  updateThemeMode,
  updateThemeId,
  updateShowApplicationTour,
  updateLatestShowedFeatures,
  updatePinnedSideBarOnMaximizedMode,
  updateDefaultOutgoingSmsBusinessProcessId,
  updateSmsIncomingInteractionRingtones,
  updateCurrentUserDashboardCardBusinessProcess,
  createCurrentUserDefaultDashboard,
  updateSupervisorSummaryTableSettings,
  setSoftPhoneSettings,
  removeUserSettings
};
